import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useAuthStore } from './auth'

const storageKey = 'coachsight.eventFavorites.v1'
function load(): Record<string, string[]> {
  try {
    const value = JSON.parse(localStorage.getItem(storageKey) || '{}')
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
    const validated: Record<string, string[]> = {}
    for (const [key, ids] of Object.entries(value)) {
      if (Array.isArray(ids) && ids.every(id => typeof id === 'string')) validated[key] = ids
    }
    return validated
  } catch { return {} }
}

export const useEventFavoritesStore = defineStore('eventFavorites', () => {
  const auth = useAuthStore()
  const favorites = ref(load())
  const userId = computed(() => String(auth.user?.details?.id || auth.user?.id || ''))
  const available = computed(() => Boolean(userId.value))
  const scope = (clubId: string, seasonId: string) => JSON.stringify([userId.value, clubId, seasonId])
  function isFavorite(clubId: string, seasonId: string, eventId: string) {
    return available.value && (favorites.value[scope(clubId, seasonId)] || []).includes(eventId)
  }
  function toggle(clubId: string, seasonId: string, eventId: string) {
    if (!available.value) return
    const key = scope(clubId, seasonId)
    const ids = favorites.value[key] || []
    favorites.value[key] = ids.includes(eventId) ? ids.filter(id => id !== eventId) : [...ids, eventId]
    try { localStorage.setItem(storageKey, JSON.stringify(favorites.value)) } catch { /* Retain the preference for this session when storage is unavailable. */ }
  }
  // Stable partition: retain the caller's current sorting within each group.
  function prioritize<T>(rows: readonly T[], favorite: (row: T) => boolean): T[] {
    return [...rows.filter(favorite), ...rows.filter(row => !favorite(row))]
  }
  return { available, isFavorite, toggle, prioritize }
})
